/**
 * Filteren en doorbladeren op een overzichtspagina, zonder de pagina te herladen.
 *
 * Hiervoor zette elke klik window.location.href. WordPress bouwde dan de hele pagina
 * opnieuw op -- hero, content, alle scripts -- om er een andere lijst kaarten in te
 * zetten, en de browser sprong daarna met een harde scroll terug naar de filterbalk.
 * Dat is wat traag aanvoelde.
 *
 * Nu wordt precies dezelfde URL met fetch opgehaald en alleen het resultatenblok
 * vervangen. Het filteren blijft dus volledig serverwerk in de composer: we vragen de
 * pagina op die de bezoeker anders bezocht zou hebben en knippen daar de lijst uit.
 * De "Alles"-link, de select en de paginanavigatie blijven daardoor gewone links die
 * het zonder JavaScript ook doen.
 *
 * Het projecten- en het nieuwsoverzicht verschillen alleen in hun selectors en in hoe
 * hun querystring eruitziet; die twee dingen komen uit de configuratie hieronder.
 *
 * Wie de lijst niet ziet verspringen krijgt na afloop te horen wat er staat; zie
 * announce() verderop.
 */

import { getLenis } from './smooth-scroll';

/**
 * Hoe lang de laadstaat minimaal blijft staan, in milliseconden.
 *
 * Zonder ondergrens flitst de skeleton voorbij: lokaal is een filterwissel vaak binnen
 * 150 ms rond, en iets dat zo kort verschijnt leest als een storing in plaats van als
 * laden. Met de ondergrens wacht de swap tot deze tijd om is; duurt het antwoord langer,
 * dan wisselt hij zodra de data er is.
 *
 * Op 0 zetten laat de lijst wisselen zodra het kan -- dat is technisch het snelst, maar
 * je ziet de skeleton dan op een snelle verbinding nauwelijks.
 */
const SKELETON_MIN_MS = 400;

/**
 * Een lege waarde hoort niet als lege parameter in de URL te blijven staan: de composers
 * lezen een ontbrekende en een lege parameter allebei als "niet gefilterd", en zonder de
 * lege resten is de URL die je deelt een stuk leesbaarder.
 */
export function setParam(url, key, value) {
  if (value) {
    url.searchParams.set(key, value);
  } else {
    url.searchParams.delete(key);
  }
}

export default function initArchiveFilter(config) {
  const filter = document.querySelector(config.filter);
  const results = document.querySelector(config.results);

  if (!filter || !results) {
    return;
  }

  // Waar het doorbladeren naartoe scrolt. Zonder opgave is dat de lijst zelf; het
  // projectenoverzicht wijst naar zijn filterbalk, want die staat er vlak boven.
  const scrollAnchor = (config.scrollTarget && document.querySelector(config.scrollTarget)) || results;
  const optionSelector = `${config.all}, ${config.option}`;

  let pending = null;

  /**
   * Het vak waarin de uitkomst van een filterwissel wordt gemeld.
   *
   * Voor wie de lijst ziet verspringen is de wissel zichtbaar: de skeleton komt en gaat.
   * Met een schermlezer gebeurde er tot nu toe hoorbaar niets -- aria-busy op het
   * resultatenblok zegt dat er iets laadt, maar niet wat eruit kwam.
   *
   * Het vak wordt hier bij het opstarten aangemaakt en blijft daarna staan. Dat is geen
   * detail: een live region die tegelijk in de DOM verschijnt en gevuld wordt, wordt
   * lang niet altijd voorgelezen. Het staat ook buiten het resultatenblok, want dat
   * wordt bij elke wissel vervangen.
   *
   * De klasse sr-only komt uit de views (Tailwind scant de scripts niet, zie de
   * content-glob in resources/styles/tailwind.config.js) -- daar staat hij al, dus hij
   * wordt gegenereerd.
   */
  const status = document.createElement('p');

  status.className = 'sr-only';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  results.parentNode.insertBefore(status, results);

  /**
   * Melden wat er na de wissel staat.
   *
   * De telling komt uit het raster dat er zojuist in is gezet; is er geen raster, dan is
   * het de lege staat. Zonder describe() in de configuratie blijft het stil: dan is er
   * geen bewoording voor dit overzicht en is een getal zonder zelfstandig naamwoord
   * slechter dan niets.
   */
  function announce() {
    if (!config.describe) {
      return;
    }

    const grid = results.querySelector('.grid');

    status.textContent = config.describe(grid ? grid.children.length : 0);
  }

  /**
   * De URL die bij de huidige stand van de besturingselementen hoort.
   *
   * Alleen de sleutels die het overzicht zelf beheert worden aangeraakt; wat er verder in
   * de querystring staat blijft staan. Een filterwissel zet je wel terug op pagina 1:
   * blijven staan op ?pag=3 levert bij een kleinere selectie een lege lijst op.
   */
  function urlForControls() {
    const url = new URL(window.location.href);

    config.buildUrl(url, filter);
    url.searchParams.delete('pag');

    return url.toString();
  }

  /**
   * De filterbalk meenemen met wat er zojuist geladen is.
   *
   * De standen komen uit het antwoord en worden niet hier uit de URL afgeleid: de server
   * kent ook de terugvalpaden voor oude links, dus dat blijft de enige plek waar bepaald
   * wordt welke knop aanstaat. De balk zelf wordt niet vervangen -- dan zou de select
   * opnieuw opgebouwd worden en de focus wegspringen op het moment dat je er net iets in
   * gekozen hebt.
   */
  function syncControls(doc) {
    const source = doc.querySelector(config.filter);

    if (!source) {
      return;
    }

    const active = new Map();

    source.querySelectorAll(optionSelector).forEach((option) => {
      const input = option.querySelector('input[type="radio"]');

      active.set(input ? input.value : '', option.classList.contains('filter-bar__option--active'));
    });

    filter.querySelectorAll(optionSelector).forEach((option) => {
      const input = option.querySelector('input[type="radio"]');
      const key = input ? input.value : '';

      if (!active.has(key)) {
        return;
      }

      const on = active.get(key);

      option.classList.toggle('filter-bar__option--active', on);
      option.classList.toggle('filter-bar__option--idle', !on);

      if (input) {
        input.checked = on;
      }
    });

    // "Alles" wist niet altijd de hele querystring -- op het projectenoverzicht houdt hij
    // de gekozen dienst vast -- dus die href hangt van de stand af en komt uit het antwoord.
    const freshAll = source.querySelector(config.all);
    const liveAll = filter.querySelector(config.all);

    if (liveAll && freshAll) {
      liveAll.setAttribute('href', freshAll.getAttribute('href'));
    }

    // Een select staat al goed als de bezoeker hem zelf net gezet heeft, maar niet na een
    // klik op "Alles" of bij terugbladeren.
    filter.querySelectorAll('select[name]').forEach((select) => {
      const fresh = source.querySelector(`select[name="${select.name}"]`);

      if (fresh && select.value !== fresh.value) {
        select.value = fresh.value;
      }
    });
  }

  /**
   * De aangeklikte knop meteen laten oplichten.
   *
   * Zonder dit blijft tijdens de laadstaat de oude knop aanstaan: je klikt "Particulier" en
   * de balk zegt nog een halve tel "Alles". De klasse komt normaal uit het antwoord, en dat
   * blijft ook zo -- syncControls() zet de stand recht zodra het er is. Dit is alleen de
   * bevestiging dat je klik is aangekomen.
   */
  function markActive(target) {
    filter.querySelectorAll(optionSelector).forEach((option) => {
      const on = option === target;

      option.classList.toggle('filter-bar__option--active', on);
      option.classList.toggle('filter-bar__option--idle', !on);
    });
  }

  /**
   * De laadstaat opbouwen: een raster met evenveel lege tegels als er nu kaarten staan.
   *
   * De blauwdruk staat als <template> in de archieftemplate, buiten het blok dat vervangen
   * wordt. Dat moet ook: Tailwind scant de scripts niet (zie de content-glob in
   * resources/styles/tailwind.config.js), dus klassen die alleen hier zouden staan worden
   * nooit gegenereerd. Het skeletraster erft zijn klassen van het echte raster, zodat de
   * kolommen en de verspringing van de tweede kolom vanzelf kloppen.
   *
   * Zonder blauwdruk gebeurt er niets bijzonders en valt het terug op het dimmen van de
   * bestaande lijst.
   */
  function showSkeleton() {
    const staand = results.querySelector('[data-archive-skeleton-grid]');

    // Er werd al gefilterd en de laadstaat staat er nog: die laten we staan.
    if (staand) {
      return staand;
    }

    const template = document.querySelector('[data-archive-skeleton]');
    const grid = results.querySelector('.grid');

    if (!template || !grid) {
      return null;
    }

    // Evenveel tegels als er nu staan houdt de hoogte gelijk. Staat de lege staat er, dan
    // is er niets om op te rekenen en tonen we er vier -- anders zie je vanuit "geen
    // resultaten" helemaal niets gebeuren.
    const count = grid.children.length || 4;

    const skeleton = document.createElement('div');

    skeleton.className = grid.className;
    skeleton.dataset.archiveSkeletonGrid = '';

    // Lege tegels zijn niets om voor te lezen.
    skeleton.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < count; i++) {
      skeleton.appendChild(template.content.cloneNode(true));
    }

    // De paginanavigatie blijft staan -- die weghalen zou de pagina onder je voeten laten
    // inklappen -- maar de lijst en een eventuele "geen resultaten"-melding gaan weg.
    Array.from(results.children).forEach((child) => {
      if (!child.querySelector('.pagination-nav')) {
        child.style.display = 'none';
      }
    });

    results.prepend(skeleton);
    results.classList.add('has-skeleton');

    return skeleton;
  }

  /**
   * De lijst vervangen door die van een andere URL.
   *
   * Een lopende aanvraag wordt afgebroken: wie snel achter elkaar filtert wil de laatste
   * keuze zien, niet het antwoord dat toevallig als laatste binnenkomt. Gaat er iets mis,
   * dan valt het terug op gewone navigatie -- liever traag dan een lijst die niet meebeweegt.
   */
  function load(url, { push = true, scroll = false } = {}) {
    if (pending) {
      pending.abort();
    }

    const controller = new AbortController();
    const startedAt = Date.now();

    pending = controller;

    results.setAttribute('aria-busy', 'true');
    results.classList.add('is-loading');

    const skeleton = showSkeleton();

    fetch(url, {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Onverwachte status ' + response.status);
        }

        return response.text();
      })
      .then(async (html) => {
        // De laadstaat minimaal even laten staan; zie SKELETON_MIN_MS. Duurde het antwoord
        // al langer, dan is er niets meer te wachten.
        const remaining = skeleton ? SKELETON_MIN_MS - (Date.now() - startedAt) : 0;

        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }

        // Tijdens dat wachten kan er alweer geklikt zijn; dan wint die klik.
        if (controller.signal.aborted) {
          return;
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(config.results);

        if (!fresh) {
          throw new Error('Geen resultaten in het antwoord');
        }

        results.innerHTML = fresh.innerHTML;
        syncControls(doc);

        if (push) {
          window.history.pushState({ archiveFilter: true }, '', url);
        }

        if (config.afterSwap) {
          config.afterSwap();
        }

        announce();

        if (scroll) {
          scrollToList(false);
        }

        pending = null;
        results.removeAttribute('aria-busy');
        results.classList.remove('is-loading', 'has-skeleton');
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        window.location.href = url;
      });
  }

  /**
   * Naar de bovenkant van de lijst.
   *
   * Lenis houdt de scroll van de site vast; er met een losse window-scroll langsheen
   * springen laat de demping achter de feiten aanlopen. Zonder Lenis -- dat is het geval
   * bij prefers-reduced-motion -- is de native scroll de juiste weg.
   */
  function scrollToList(immediate) {
    const lenis = getLenis();

    if (lenis) {
      lenis.scrollTo(scrollAnchor, { immediate });
      return;
    }

    scrollAnchor.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  // Een klik met ctrl/cmd/shift of met de middelste knop hoort een nieuw tabblad te
  // openen; die laten we met rust.
  function isPlainClick(event) {
    return !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0;
  }

  filter.addEventListener('change', (event) => {
    if (!event.target.matches(config.controls)) {
      return;
    }

    // Een select toont zijn eigen keuze al; alleen de knoppenrij heeft de bevestiging nodig.
    const option = event.target.closest(config.option);

    if (option) {
      markActive(option);
    }

    load(urlForControls());
  });

  filter.addEventListener('click', (event) => {
    const link = event.target.closest(config.all);

    if (!link || !isPlainClick(event)) {
      return;
    }

    event.preventDefault();
    markActive(link);
    load(link.href);
  });

  // De paginanavigatie zit in het stuk dat vervangen wordt, dus die klik wordt op de
  // container opgevangen in plaats van op de links zelf.
  results.addEventListener('click', (event) => {
    const link = event.target.closest('.pagination-nav a');

    if (!link || !isPlainClick(event)) {
      return;
    }

    event.preventDefault();
    load(link.href, { scroll: true });
  });

  window.addEventListener('popstate', () => {
    load(window.location.href, { push: false });
  });

  // Wie met een filter of een paginanummer in de URL binnenkomt -- via een tag op een
  // projectcard bijvoorbeeld -- stond hiervoor meteen bij de lijst. Dat blijft zo.
  const entry = new URL(window.location.href).searchParams;

  if ((config.entry || []).some((param) => entry.get(param))) {
    scrollToList(true);
  }
}
